import React from 'react';
import { 
    TextField, 
    Checkbox, 
    FormControlLabel, 
    Grid, 
    Box, 
    Tooltip,
    Typography,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';

// Reusable Setting Item Component with help tooltip
const SettingItem = ({ helpText, children }) => {
    const [showHelp, setShowHelp] = React.useState(false);
    
    return (
        <Box sx={{ 
            width: '100%',
            mb: { xs: 1, sm: 1.5 }
        }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
                {helpText && (
                    <IconButton 
                        size="small" 
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ 
                            mt: 0.5,
                            color: 'action.active',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helpText && (
                <Collapse in={showHelp}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            p: 1,
                            mt: 0.5,
                            lineHeight: 1.4
                        }}
                    >
                        {helpText}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
};

// Section Card Component
const SettingsSection = ({ title, icon: Icon, children }) => {
    return (
        <Card 
            elevation={0}
            sx={{ 
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <CardContent sx={{ 
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2 },
                '&:last-child': { pb: { xs: 2, sm: 2 } }
            }}>
                {children}
            </CardContent>
        </Card>
    );
};

// Responsive TextField Component
const ResponsiveTextField = ({ name, value, onChange, label, disabled, helpText, type = 'text', error, inputProps, customOnChange }) => {
    return (
        <SettingItem helpText={helpText}>
            <TextField
                fullWidth
                size="medium"
                label={label}
                name={name}
                value={value ?? ''}
                onChange={customOnChange || onChange}
                variant="outlined"
                disabled={disabled}
                type={type}
                error={error}
                inputProps={inputProps}
                sx={{
                    '& .MuiInputBase-input': {
                        fontSize: { xs: '1rem', sm: '0.95rem' },
                        py: { xs: 1.5, sm: 1.25 }
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.95rem', sm: '0.9rem' }
                    }
                }}
            />
        </SettingItem>
    );
};

// Responsive Checkbox Component
const ResponsiveCheckbox = ({ name, checked, onChange, label, disabled, helpText, warning }) => {
    return (
        <SettingItem helpText={helpText}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                    control={
                        <Checkbox 
                            name={name}
                            checked={checked || false}
                            onChange={onChange}
                            disabled={disabled}
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: { xs: 24, sm: 24 }
                                }
                            }}
                        />
                    }
                    label={
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                color: disabled ? 'text.disabled' : 'text.primary'
                            }}
                        >
                            {label}
                        </Typography>
                    }
                    sx={{
                        m: 0,
                        flex: 1,
                        '& .MuiFormControlLabel-label': {
                            flex: 1
                        }
                    }}
                />
                {warning && (
                    <Tooltip title={warning}>
                        <WarningIcon color='error' sx={{ ml: 1, fontSize: 20 }} />
                    </Tooltip>
                )}
            </Box>
        </SettingItem>
    );
};

const QRCodeKeysTab = ({ 
    settings, 
    handleInputChange,
    setSettings,
    setIsModified,
    disableQrCodeKeysAliases,
    masterQrCodeKeyAliases,
    masterTenantNameForQrCodeKeys
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* About QR Code Keys */}
            <SettingsSection title="About QR Code Keys" icon={InfoIcon}>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' },
                        lineHeight: 1.6
                    }}
                >
                    QR Code Keys allow you to generate and manage unique QR codes that can be used to open gates or doors. 
                    When scanned by the kiosk, the system will verify the code and trigger the configured gate action.
                </Typography>
            </SettingsSection>

            {/* QR Code Keys Settings */}
            <SettingsSection title="QR Code Keys Settings" icon={QrCodeIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                        <ResponsiveCheckbox
                            name="SettingEnableQRCodeKeys"
                            checked={settings.SettingEnableQRCodeKeys}
                            onChange={handleInputChange}
                            label="Enable QR Code Keys"
                            helpText="Enables the QR Code Key functionality on this kiosk"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingIncludeQRCodeKeyButton"
                            checked={settings.SettingIncludeQRCodeKeyButton}
                            onChange={handleInputChange}
                            label="Include QR Code Key button UI (recommended)"
                            disabled={!settings.SettingEnableQRCodeKeys}
                            helpText="Shows a button on the kiosk UI with a visual guide to scan QR codes. If disabled, QR codes are scanned automatically when detected."
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingAllowAliasesForQrCodeKeys"
                            checked={masterQrCodeKeyAliases ?? (settings.SettingAllowAliasesForQrCodeKeys || false)}
                            onChange={handleInputChange}
                            label="Enable aliases for QR Code Keys"
                            disabled={!settings.SettingEnableQRCodeKeys || disableQrCodeKeysAliases}
                            helpText="Assign QR Code Keys to Aliases to notify specific people on successful scan and entry."
                            warning={disableQrCodeKeysAliases ? `QR Code Keys are shared from ${masterTenantNameForQrCodeKeys}. Switch to that kiosk to change this setting.` : null}
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* QR Code Keys Configuration */}
            <SettingsSection title="Configuration" icon={SettingsIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingQRCodeKeysSecondsBeforeReads"
                            value={settings.SettingQRCodeKeysSecondsBeforeReads}
                            onChange={handleInputChange}
                            label="Wait time (seconds) between scans"
                            disabled={!settings.SettingEnableQRCodeKeys}
                            helpText="Time before rescanning the same QR code. Helps prevent duplicate scans."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="securityLevel"
                            value={settings.securityLevel}
                            label="Security Level"
                            type="number"
                            disabled={!settings.SettingEnableQRCodeKeys}
                            inputProps={{ min: 1 }}
                            error={settings.securityLevel !== '' && settings.securityLevel !== null && 
                                (isNaN(parseInt(settings.securityLevel)) || parseInt(settings.securityLevel) <= 0)}
                            customOnChange={(e) => {
                                const value = e.target.value;
                                setSettings(prev => ({
                                    ...prev,
                                    securityLevel: value === '' ? null : value
                                }));
                                setIsModified(true);
                            }}
                            helpText="Optional. QR Code Keys must have an equal or higher security level to access this kiosk."
                        />
                    </Grid>
                </Grid>
            </SettingsSection>
        </Box>
    );
};

export default QRCodeKeysTab;
